import React, { useCallback, useMemo } from 'react';
import { Grid } from '@mui/material';
import {
  ActivityAssignmentData,
  StepWithResponseData,
} from 'hooks/activities/useQueryActivityAssignments';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';
import {
  QuestionChoice,
  QuestionType,
  SaveStepResponseInput,
  StepResponse,
} from '__generated__/graphql';
import TextQuestion from 'pages/Activities/ActivityAssignmentStep/StepQuestion/TextQuestion/TextQuestion';
import ChoiceQuestion from 'pages/Activities/ActivityAssignmentStep/StepQuestion/ChoiceQuestion/ChoiceQuestion';
import useMutationSaveStepResponse from 'hooks/activities/useMutationSaveStepResponse';
import { hasPermissions } from 'config/ability';
import { useAuthContext } from 'context/auth.context';

function getQuestionAnswerValue(
  stepWithResponse: StepWithResponseData
): string | QuestionChoice[] | undefined {
  const response = stepWithResponse.response;
  if (!response || !stepWithResponse.step.question) {
    return undefined;
  }

  switch (stepWithResponse.step.question.type) {
    case QuestionType.Choice:
      return (response.answers || [])
        .map((x) => x.selectedChoice)
        .filter((x): x is QuestionChoice => !!x);
    case QuestionType.Text:
      return response.textAnswer != null ? response.textAnswer : undefined;
    default:
      return undefined;
  }
}

interface StepQuestionProps {
  activityAssignment: ActivityAssignmentData;
  stepWithResponse: StepWithResponseData;
  onChange?: (newValue: StepResponse) => void;
}

type Props = StepQuestionProps;

const StepQuestion = ({ activityAssignment, stepWithResponse, onChange }: Props) => {
  const question = stepWithResponse.step.question;
  const questionType = question?.type;
  const answerValue = useMemo(
    () => questionType && getQuestionAnswerValue(stepWithResponse),
    [questionType, stepWithResponse]
  );
  const response = stepWithResponse.response;
  const [saveStepResponse] = useMutationSaveStepResponse();
  const { ability, user, userTenantId } = useAuthContext();

  const submitStepResponse = useCallback(
    async (
      data: Omit<SaveStepResponseInput, 'activityAssignmentId' | 'stepId' | 'stepResponseId'>
    ) => {
      await saveStepResponse({
        variables: {
          input: {
            activityAssignmentId: activityAssignment.id,
            stepId: stepWithResponse.step.id,
            stepResponseId: response?.id,
            ...data,
          },
        },
      });
    },
    [activityAssignment.id, response?.id, saveStepResponse, stepWithResponse.step.id]
  );

  const handleTextChange = useCallback(
    async (newValue: string) => {
      await submitStepResponse({
        textAnswer: newValue,
      });
    },
    [submitStepResponse]
  );

  const handleChoiceChange = useCallback(
    async (newValues: QuestionChoice[]) => {
      if (!question) {
        return;
      }
      await submitStepResponse({
        answers: newValues.map((choice) => ({
          id: question.isMultiple
            ? response?.answers?.find((answer) => answer.selectedChoice?.id === choice.id)?.id
            : response?.answers?.[0]?.id,
          questionId: question.id,
          selectedChoiceId: choice.id,
        })),
      });
    },
    [question, response?.answers, submitStepResponse]
  );

  // Return null if there is no question or user does not have 'Complete' permissions
  if (
    !question ||
    (!hasPermissions(ability, 'complete', 'Step', { tenantId: userTenantId }) &&
      !hasPermissions(ability, 'complete', 'Step', { stepId: stepWithResponse.step.id }) &&
      !hasPermissions(ability, 'complete', 'Step', {
        userId: user?.user?.id,
      }) &&
      !hasPermissions(ability, 'complete', 'StepResponse', { stepResponseId: stepWithResponse.id }))
  ) {
    return null;
  }

  return (
    <Grid container sx={{ marginTop: '3.4375rem' }}>
      <OverviewDetail title={question.questionDetail}>
        {questionType === QuestionType.Text && (
          <TextQuestion
            step={stepWithResponse.step}
            question={question}
            onChange={handleTextChange}
            value={answerValue as string | undefined}
          />
        )}
        {questionType === QuestionType.Choice && (
          <ChoiceQuestion
            step={stepWithResponse.step}
            question={question}
            onChange={handleChoiceChange}
            value={(answerValue || []) as QuestionChoice[]}
          />
        )}
      </OverviewDetail>
    </Grid>
  );
};

export default StepQuestion;
